import { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tokens } from '../theme/netScotlandTheme';

const BRAND_ORANGE = tokens.color.brandOrange;
const BRAND_ORANGE_HOVER = '#D67F00'; // hover step kept local; UI-SPEC defines no token

/**
 * The subscribable iCal feed served by `/api/calendar.ics` (a live
 * `text/calendar` feed, not a static snapshot). To AUTO-SYNC, a calendar
 * app needs the feed URL to poll — not a one-off downloaded file. This
 * component surfaces that URL (copy + webcal/Google one-click) instead of
 * the previous button that just downloaded the .ics once (CU 86ba92a56).
 */
const FEED_PATH = '/api/calendar.ics';

function feedUrls() {
    // Computed at click time from the live origin so the feed works on
    // prod, preview and local without any per-deploy config. Guarded for
    // SSR / non-browser test environments.
    const origin =
        typeof window !== 'undefined' && window.location
            ? window.location.origin
            : '';
    const host =
        typeof window !== 'undefined' && window.location
            ? window.location.host
            : '';
    const https = `${origin}${FEED_PATH}`;
    // webcal:// is the cross-app "subscribe" scheme (Apple Calendar, Outlook,
    // many others register it) — same path, scheme swapped.
    const webcal = host ? `webcal://${host}${FEED_PATH}` : '';
    // Google Calendar "add by URL" deep link.
    const google = `https://calendar.google.com/calendar/r/settings/addbyurl?cid=${encodeURIComponent(
        https
    )}`;
    return { https, webcal, google };
}

interface Props {
    /** Extra sx applied to the trigger button (e.g. top margin in empty state). */
    sx?: object;
}

/**
 * Orange "Subscribe to this calendar" CTA. Opens a dialog with the feed URL
 * to add to Google / Apple / Outlook so the calendar stays in sync, plus a
 * one-time .ics download as a fallback.
 */
export function SubscribeCalendarButton({ sx }: Props) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const urls = open ? feedUrls() : { https: '', webcal: '', google: '' };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(urls.https);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API unavailable (insecure context / older browser);
            // the URL is selectable in the field as a fallback.
        }
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                variant="contained"
                sx={{
                    bgcolor: BRAND_ORANGE,
                    color: '#fff',
                    '&:hover': { bgcolor: BRAND_ORANGE_HOVER },
                    ...sx,
                }}
            >
                Subscribe to this calendar
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="subscribe-calendar-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="subscribe-calendar-title">
                    Subscribe to the calendar
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Add this calendar to Google, Apple or Outlook and it
                        will stay in sync automatically — new and updated events
                        appear without you having to re-import anything.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Calendar feed URL
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            value={urls.https}
                            fullWidth
                            size="small"
                            InputProps={{ readOnly: true }}
                            inputProps={{ 'aria-label': 'Calendar feed URL' }}
                            onFocus={e => e.target.select()}
                        />
                        <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                            <IconButton
                                onClick={handleCopy}
                                aria-label="Copy calendar feed URL"
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <Button href={urls.webcal} variant="outlined" fullWidth>
                            Add to Apple Calendar
                        </Button>
                        <Button
                            href={urls.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            fullWidth
                        >
                            Add to Google Calendar
                        </Button>
                    </Stack>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                    >
                        Or add it manually: in Google Calendar choose{' '}
                        <em>Other calendars → From URL</em>; in Apple Calendar
                        choose <em>File → New Calendar Subscription</em>, and
                        paste the URL above.
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <Link href={FEED_PATH} variant="caption">
                            Prefer a one-time download? Get the .ics file
                        </Link>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
